// src/components/Workspace.tsx
import { useEffect, useRef, useState } from 'react';
import { AiAssistant } from '../common/AiAssistant';
import { Canvas } from './Canvas';
import { EvaluatePanel } from '../result/EvaluatePanel';
import { BuildPalette, StrategyLibraryFlyout } from './Sidebar';
import type { StrategyNode } from '../../../types/BaseNode';
import type { StrategyFamily } from '../../../types/Strategy';
import { getSavedStrategies, deleteSavedStrategy } from '../../../utils/storage';
import { MultiEvaluatePanel } from '../result/MultiEvaluatePanel';
import { MonitorOverview } from '../monitor/MonitorOverview';
import { ActiveAlertsOverview } from '../monitor/ActiveAlertsOverview';
import { UserGuide } from '../common/UserGuide';
import { TranslationStatusHint } from './TranslationStatusHint';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { useWorkspaceState, type WorkspaceState } from '../../../hooks/useWorkspaceState';
import { useStrategyOperations } from '../../../hooks/useStrategyOperations';
import { MonitorContext } from '../../../contexts/MonitorContext';
import { StrategyProvider } from '../../../contexts/StrategyContext';
import { ApiService } from '../../../services/ApiService';
import { GlassTabs, type TabOption } from '../GlassTabs';
import './Workspace.css';
import '../../../styles/MeepMeep.css'; // Corrected path

interface WorkspaceProps {
    workspaceId: string;
    state: Partial<WorkspaceState> | undefined;
    setState: (newState: WorkspaceState) => void;
    deepLinkStrategyId?: number | null;
    onDeepLinkLoaded?: () => void;
    isMonitorTab?: boolean;
    /** Called when user clicks "Load" in Monitor/Alerts views – Universe routes this to the Monitor tab */
    onLoadMonitorStrategy?: (strategyJson: string, name: string, monitorId?: number, conditionJson?: string) => void;
    /** Strategy payload waiting to be loaded into this Monitor tab */
    pendingMonitorLoad?: { strategyJson: string; name: string; monitorId?: number; conditionJson?: string; } | null;
    /** Called once the pending load has been consumed */
    onPendingMonitorLoadConsumed?: () => void;
}


export function Workspace({ workspaceId, state, setState, deepLinkStrategyId, onDeepLinkLoaded, isMonitorTab, onLoadMonitorStrategy, pendingMonitorLoad, onPendingMonitorLoadConsumed }: WorkspaceProps) {

    // Initialize workspace state with custom hook
    const {
        strategies,
        setStrategies,
        activeTab,
        setActiveTab,
        activeStrategyId,
        setActiveStrategyId,
        globalStart,
        setGlobalStart,
        globalEnd,
        setGlobalEnd,
        editingId,
        setEditingId,
        errorNode,
        setWorkspaceState,
        setStrategyResult,
        removeStrategyResult,
        strategyFamily,
        setStrategyFamily,
    } = useWorkspaceState(workspaceId, state, setState);

    // Initialize strategy operations with custom hook
    const {
        addNewStrategy,
        createStrategyFromNode,
        loadFromMonitor: handleLoadFromMonitor,
        removeStrategy,
        updateStrategy,
        evaluateStrategy: handleEvaluate,
        runQuantArcBacktest,
        cancelQuantArcBacktest,
        cancelMeepMeepJob,
        refreshOverlays,
        undoHandlers,
    } = useStrategyOperations({
        strategies,
        setStrategies,
        setActiveStrategyId,
        setActiveTab,
        setWorkspaceState,
        setStrategyResult,
        removeStrategyResult,
        globalStart,
        globalEnd,
        strategyFamily,
        initialState: state,
    });

    // Deep link: load strategy from URL param on first mount
    const deepLinkHandled = useRef(false);
    useEffect(() => {
        if (!deepLinkStrategyId || deepLinkHandled.current) return;
        deepLinkHandled.current = true;

        ApiService.getStrategyById(deepLinkStrategyId)
            .then(({ data }) => {
                if (data?.StrategyJson) {
                    handleLoadFromMonitor(
                        data.StrategyJson,
                        data.Name,
                        data.ID,
                        data.ConditionJson
                    );
                }
            })
            .catch((err) => console.error('Deep link load failed:', err))
            .finally(() => onDeepLinkLoaded?.());
    }, [deepLinkStrategyId, handleLoadFromMonitor, onDeepLinkLoaded]);

    // Pending monitor load: triggered when user clicks Load in Monitor/Alerts panels in another tab
    const lastHandledMonitorLoad = useRef<any>(null);
    useEffect(() => {
        if (!pendingMonitorLoad || lastHandledMonitorLoad.current === pendingMonitorLoad) return;
        lastHandledMonitorLoad.current = pendingMonitorLoad;

        handleLoadFromMonitor(
            pendingMonitorLoad.strategyJson,
            pendingMonitorLoad.name,
            pendingMonitorLoad.monitorId,
            pendingMonitorLoad.conditionJson
        );
        onPendingMonitorLoadConsumed?.();
    }, [pendingMonitorLoad, handleLoadFromMonitor, onPendingMonitorLoadConsumed]);

    // Refresh overlays when dates change
    useEffect(() => {
        const activeStrategy = strategies.find(s => s.id === activeStrategyId);
        if (activeStrategy) {
            refreshOverlays(activeStrategy);
        }
    }, [globalStart, globalEnd, activeStrategyId, refreshOverlays]);

    const [savedStrategies, setSavedStrategies] = useState(getSavedStrategies());

    const getActiveStrategy = () => strategies.find((s) => s.id === activeStrategyId) ?? null;

    // handleEvaluate is now provided by useStrategyOperations hook


    const activeStrategy = getActiveStrategy();
    useEffect(() => {
        if (activeStrategy?.strategyFamily && activeStrategy.strategyFamily !== strategyFamily) {
            setStrategyFamily(activeStrategy.strategyFamily);
        }
    }, [activeStrategy?.id, activeStrategy?.strategyFamily, strategyFamily, setStrategyFamily]);

    const showAll = activeTab === 'results' && activeStrategyId === 'all';
    const activeStrategyIsEvaluating = activeStrategy?.loadingMode === 'evaluate';
    const activeStrategyIsRunningQuantArc = activeStrategy?.loadingMode === 'quantarc';
    const activeStrategyIsRunningMeepMeep = activeStrategy?.loadingMode === 'meepmeep';
    const activeStrategyBuildLocked = activeStrategyIsRunningQuantArc || activeStrategyIsRunningMeepMeep;
    const anySelectedStrategyLoading = strategies.some((s) => Boolean(s.selectedInAll && s.isLoading));

    const updateNodes: React.Dispatch<React.SetStateAction<StrategyNode[]>> = (n) => {
        if (!activeStrategy) return;

        const prevNodes = activeStrategy.nodes;
        const newNodes = typeof n === 'function' ? n(prevNodes) : n;

        const newRootTitle = newNodes[0]?.title;
        const currentStrategyName = activeStrategy.name;

        updateStrategy(activeStrategy.id, {
            nodes: newNodes,
            ...(newRootTitle && newRootTitle !== currentStrategyName
                ? { name: newRootTitle }
                : {}),
        });
    };

    const updateActiveStrategyNodeMetadata = (metadata?: Record<string, unknown>) => {
        if (!activeStrategy || !metadata) return;
        setStrategyResult(activeStrategy.id, {
            nodeMetadata: {
                ...(activeStrategy.nodeMetadata ?? {}),
                ...metadata,
            },
        });
    };

    const extractNodeCount = (value: unknown): number | null => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        }
        if (!value || typeof value !== 'object') return null;
        const record = value as Record<string, unknown>;
        const candidates = [record.count, record.row_count, record.output_row_count, record.result_count];
        for (const candidate of candidates) {
            if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
            if (typeof candidate === 'string') {
                const parsed = Number(candidate);
                if (Number.isFinite(parsed)) return parsed;
            }
        }
        return null;
    };

    const normalizePreviewMetadata = (response: unknown, nodeId: string): Record<string, unknown> | undefined => {
        if (!response || typeof response !== 'object') return undefined;
        const record = response as Record<string, unknown>;
        const rawMetadata = record.node_metadata;
        const normalized: Record<string, unknown> = {};

        if (rawMetadata && typeof rawMetadata === 'object' && !Array.isArray(rawMetadata)) {
            for (const [key, value] of Object.entries(rawMetadata as Record<string, unknown>)) {
                const count = extractNodeCount(value);
                if (count !== null && value && typeof value === 'object') {
                    normalized[key] = { ...(value as Record<string, unknown>), count };
                } else if (count !== null) {
                    normalized[key] = { count };
                } else {
                    normalized[key] = value;
                }
            }
        }

        const rootCount =
            typeof record.row_count === 'number' ? record.row_count
                : Array.isArray(record.data) ? record.data.length
                    : null;
        if (rootCount !== null && normalized[nodeId] === undefined) {
            normalized[nodeId] = { count: rootCount };
        }

        return Object.keys(normalized).length > 0 ? normalized : undefined;
    };

    const updateActiveStrategyPreviewResult = (response: unknown, nodeId: string) => {
        if (!activeStrategy || !response || typeof response !== 'object') return;

        const record = response as Record<string, unknown>;
        const resultData = record.data ?? (record.nature === 'insight' ? record : null);
        const metadata = normalizePreviewMetadata(response, nodeId);

        setStrategyResult(activeStrategy.id, {
            result: resultData,
            error: null,
            nature: typeof record.nature === 'string' ? record.nature : 'generic',
            display_type: typeof record.display_type === 'string' ? record.display_type : undefined,
            categorical_labels: (record.categorical_labels as Record<number, string> | undefined) ?? undefined,
            backendStats: (record.stats as Record<string, number | null> | null | undefined) ?? null,
            chartData: (record.chart_data as any[] | null | undefined) ?? null,
            chartSeriesLabels: (record.chart_series_labels as Record<string, string> | null | undefined) ?? null,
            nodeMetadata: metadata
                ? {
                    ...(activeStrategy.nodeMetadata ?? {}),
                    ...metadata,
                }
                : activeStrategy.nodeMetadata,
            parityReport: record.parity_report ?? null,
            isLoading: false,
            loadingMode: null,
        });
    };

    const handleStrategyFamilyChange = (family: StrategyFamily) => {
        setStrategyFamily(family);
        if (activeStrategy) {
            updateStrategy(activeStrategy.id, { strategyFamily: family });
        }
    };





    const tabOptions: TabOption<'build' | 'results' | 'monitor' | 'alerts'>[] = [
        { id: 'build', label: '🧱 Build' },
        { id: 'results', label: '📊 Results' },
        { id: 'monitor', label: '🔍 Monitor' },
        { id: 'alerts', label: '🚨 Alerts' },
    ];

    return (
        <StrategyProvider value={{ strategies }}>
            <div className="w-full h-full flex flex-col">
                {/* Tab Header with Strategy Family Toggle */}
                <div className="workspace-header-container">
                    <GlassTabs
                        className="workspace-main-tabs"
                        tabs={tabOptions}
                        activeTab={activeTab === 'guide' ? 'build' : activeTab}
                        onChange={(id) => setActiveTab(id)}
                    />

                    <div className="workspace-header-actions">
                        {activeTab === 'build' && (
                            <div className="work-mode-toggle-container">
                                <button
                                    className={`work-mode-btn ${strategyFamily === 'base' ? 'active' : ''}`}
                                    onClick={() => handleStrategyFamilyChange('base')}
                                >
                                    Standard
                                </button>
                                <button
                                    className={`work-mode-btn ${strategyFamily === 'quantarc' ? 'active' : ''}`}
                                    onClick={() => handleStrategyFamilyChange('quantarc')}
                                >
                                    QuantArc
                                </button>
                                <button
                                    className={`work-mode-btn microstructure ${strategyFamily === 'meepmeep' ? 'active' : ''}`}
                                    onClick={() => handleStrategyFamilyChange('meepmeep')}
                                >
                                    ⚡ MeepMeep
                                </button>
                            </div>
                        )}

                        <button
                            type="button"
                            className={`workspace-guide-btn ${activeTab === 'guide' ? 'active' : ''}`}
                            onClick={() => setActiveTab('guide')}
                        >
                            📖 Guide
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div 
                    className={`workspace-main-content ${strategyFamily === 'meepmeep' ? 'microstructure-mode' : ''}`}
                    style={{ display: 'flex', flexGrow: 1, paddingBottom: activeTab === 'build' ? '120px' : 0, overflow: 'hidden' }}
                >
                    {/* Strategy Tab Sidebar - Only for Build/Results */}
                    {['build', 'results'].includes(activeTab) && (
                        <div className="strategy-sidebar">
                            {activeTab === 'results' && (
                                <div
                                    onClick={() => setActiveStrategyId('all')}
                                    className={`all-strategies-btn ${activeStrategyId === 'all' ? 'active' : ''}`}
                                >
                                    <span>✅</span>
                                    <span>All</span>
                                </div>
                            )}
                            {strategies.map((s) => (
                                <div
                                    key={s.id}
                                    className={`strategy-item ${activeStrategyId === s.id ? 'active' : ''}`}
                                >
                                    {editingId === s.id ? (
                                        <input
                                            type="text"
                                            autoFocus
                                            defaultValue={s.name}
                                            className="strategy-name-input"
                                            onBlur={(e) => updateStrategy(s.id, { name: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    updateStrategy(s.id, { name: (e.target as HTMLInputElement).value });
                                                    const strat = strategies.find((x) => x.id === s.id);
                                                    if (strat?.nodes?.[0]) {
                                                        const value = (e.target as HTMLInputElement).value;
                                                        const root = { ...strat.nodes[0], title: value };
                                                        updateStrategy(s.id, { nodes: [root] });
                                                    }
                                                    setEditingId(null);
                                                } else if (e.key === 'Escape') {
                                                    setEditingId(null);
                                                }
                                            }}
                                        />
                                    ) : (
                                        <div
                                            onClick={() => setActiveStrategyId(s.id)}
                                            onDoubleClick={() => setEditingId(s.id)}
                                            className="strategy-item-content"
                                            title={s.name}
                                        >
                                            {s.name}
                                        </div>
                                    )}

                                    <button
                                        onClick={() => removeStrategy(s.id)}
                                        className="delete-strategy-btn"
                                        title="Delete Strategy"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}

                            {activeTab === 'build' && !isMonitorTab && (
                                <button onClick={addNewStrategy} className="add-strategy-btn">
                                    <span>➕</span>
                                    <span>Add Strategy</span>
                                </button>
                            )}

                            {activeTab === 'build' && (
                                <BuildPalette strategyFamily={strategyFamily} />
                            )}
                        </div>
                    )}

                    {/* Right Panel */}
                    <div style={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
                        {activeTab === 'build' && (
                            activeStrategy ? (
                                <>
                                    {/* Canvas Area */}
                                    <div style={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
                                        <TranslationStatusHint
                                            rootNode={activeStrategy.nodes[0] ?? null}
                                            isRunning={activeStrategyIsRunningQuantArc || activeStrategyIsRunningMeepMeep}
                                            jobStatus={activeStrategy?.quantArcJobStatus ?? activeStrategy?.meepMeepJobStatus as any}
                                            loadingMode={activeStrategy?.loadingMode}
                                            meepMeepJobStage={activeStrategy?.meepMeepJobStage}
                                            meepMeepJobMessage={activeStrategy?.meepMeepJobMessage}
                                            meepMeepCurrentDayCount={activeStrategy?.meepMeepCurrentDayCount}
                                            meepMeepTotalDayCount={activeStrategy?.meepMeepTotalDayCount}
                                            meepMeepProgressPct={activeStrategy?.meepMeepProgressPct}
                                            onRunQuantArc={() => runQuantArcBacktest(activeStrategy)}
                                            onCancelQuantArc={() => cancelQuantArcBacktest(activeStrategy)}
                                            onCancelMeepMeep={() => cancelMeepMeepJob(activeStrategy)}
                                        />
                                        <MonitorContext.Provider value={{
                                            monitorId: activeStrategy.monitorId,
                                            conditionRootId: activeStrategy.conditionRootId,
                                            setConditionRootId: (id) => updateStrategy(activeStrategy.id, { conditionRootId: id })
                                        }}>
                                            <ErrorBoundary fallbackMessage="There was a problem rendering the strategy builder canvas.">
                                                <Canvas
                                                    nodes={activeStrategy.nodes}
                                                    setNodes={updateNodes}
                                                    onQuickEvaluate={() => handleEvaluate(activeStrategy)}
                                                    isEvaluating={Boolean(activeStrategyIsEvaluating)}
                                                    onSubtreeEvaluate={(node) => handleEvaluate(activeStrategy, node)}
                                                    onCreateStrategyFromNode={createStrategyFromNode}
                                                    isLocked={activeStrategyBuildLocked}
                                                    start={globalStart}
                                                    end={globalEnd}
                                                    setStart={setGlobalStart}
                                                    setEnd={setGlobalEnd}
                                                    onStrategySaved={() => setSavedStrategies(getSavedStrategies())}
                                                    errorNode={errorNode}
                                                    undoHandler={undoHandlers[activeStrategy.id]}
                                                    nodeMetadata={activeStrategy.nodeMetadata}
                                                    onNodeMetadataUpdate={updateActiveStrategyNodeMetadata}
                                                    onPreviewResultUpdate={updateActiveStrategyPreviewResult}
                                                    strategyFamily={strategyFamily}
                                                />
                                            </ErrorBoundary>
                                        </MonitorContext.Provider>
                                        <StrategyLibraryFlyout
                                            savedStrategies={savedStrategies}
                                            onDeleteStrategy={(name) => {
                                                deleteSavedStrategy(name);
                                                setSavedStrategies(getSavedStrategies());
                                            }}
                                        />



                                        {/* JSON Tools */}








                                    </div>
                                </>
                            ) : (
                                <div style={{ padding: '2rem', fontSize: '1.1rem', color: 'var(--text-muted)' }}>
                                    🧪 No strategy selected. <br />
                                    ➕ Click “Add” on the left to begin building your first strategy.
                                </div>
                            )
                        )}


                        {activeTab === 'results' && (
                            showAll ? (
                                <>
                                    {/* ✅ Checkbox panel */}
                                    <div className="p-md">
                                        <strong>Included Strategies:</strong>
                                        {strategies.map((s) => (
                                            <div key={s.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!s.selectedInAll}
                                                    onChange={(e) =>
                                                        updateStrategy(s.id, { selectedInAll: e.target.checked })
                                                    }
                                                />
                                                <select
                                                    value={s.displayType || "line"}
                                                    onChange={(e) =>
                                                        updateStrategy(s.id, { displayType: e.target.value as "line" | "bar" | "arrow" })
                                                    }
                                                >
                                                    <option value="line">Line</option>
                                                    <option value="bar">Bar</option>
                                                    <option value="arrow">Arrow</option>
                                                </select>

                                                <span style={{ marginLeft: '0.5rem' }}>{s.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* ✅ Evaluate Panel for combined results */}
                                    <div style={{ width: "85vw", height: "100vh", overflow: "hidden" }}>
                                        <MultiEvaluatePanel
                                            isLoading={anySelectedStrategyLoading}
                                            onEvaluate={async () => {
                                                await Promise.all(
                                                    strategies
                                                        .filter((s) => s.selectedInAll && s.nodes.length > 0)
                                                        .map((s) => handleEvaluate(s))
                                                );
                                            }}
                                        />
                                    </div>





                                </>
                            ) : (
                                activeStrategy && (
                                    <EvaluatePanel
                                        rootNode={activeStrategy.nodes[0] || null}
                                        result={activeStrategy.result || null}
                                        error={activeStrategy.error || null}
                                        isLoading={Boolean(activeStrategy?.isLoading)}
                                        onEvaluate={(override) => handleEvaluate(activeStrategy, override)}
                                        onCancelMeepMeep={() => cancelMeepMeepJob(activeStrategy)}
                                        overlays={activeStrategy.overlays || []}
                                        setOverlays={(overlays) => updateStrategy(activeStrategy.id, { overlays })}
                                        nature={activeStrategy.nature}
                                        display_type={activeStrategy.display_type}
                                        globalStart={globalStart}
                                        globalEnd={globalEnd}
                                        strategyName={activeStrategy.name}
                                        categorical_labels={activeStrategy.categorical_labels}
                                        aiReport={activeStrategy.aiReport}
                                        setAiReport={(r) => updateStrategy(activeStrategy.id, { aiReport: r })}
                                        activeWidgets={activeStrategy.activeWidgets}
                                        setActiveWidgets={(w) => updateStrategy(activeStrategy.id, { activeWidgets: w })}
                                        backendStats={activeStrategy.backendStats}
                                        chartData={activeStrategy.chartData}
                                        chartSeriesLabels={activeStrategy.chartSeriesLabels}
                                        parityReport={activeStrategy.parityReport}
                                        loadingMode={activeStrategy.loadingMode}
                                        meepMeepJobMessage={activeStrategy.meepMeepJobMessage}
                                        meepMeepJobStage={activeStrategy.meepMeepJobStage}
                        meepMeepCurrentDayCount={activeStrategy.meepMeepCurrentDayCount}
                        meepMeepTotalDayCount={activeStrategy.meepMeepTotalDayCount}
                        meepMeepProgressPct={activeStrategy.meepMeepProgressPct}
                        meepMeepRequestTree={activeStrategy.meepMeepRequestTree}
                        selectedMeepMeepDate={activeStrategy.selectedMeepMeepDate ?? null}
                        setSelectedMeepMeepDate={(date) => setStrategyResult(activeStrategy.id, { selectedMeepMeepDate: date })}
                     />
                                )
                            )
                        )}

                        {activeTab === 'monitor' && (
                            <MonitorOverview onLoadStrategy={(sj: string, name: string, mid: number, cj: string) => {
                                if (onLoadMonitorStrategy) {
                                    onLoadMonitorStrategy(sj, name, mid, cj);
                                } else {
                                    handleLoadFromMonitor(sj, name, mid, cj);
                                }
                            }} />
                        )}

                        {activeTab === 'alerts' && (
                            <ActiveAlertsOverview onLoadStrategy={(sj: string, name: string, mid: number) => {
                                if (onLoadMonitorStrategy) {
                                    onLoadMonitorStrategy(sj, name, mid);
                                } else {
                                    handleLoadFromMonitor(sj, name, mid);
                                }
                            }} />
                        )}

                        {activeTab === 'guide' && (
                            <UserGuide onLoadTemplate={(template, name) => {
                                // Reuse logic: json stringify the node structure
                                handleLoadFromMonitor(JSON.stringify(template), name);
                            }} />
                        )}

                    </div>
                </div>

                {/* AI Assistant Bottom Bar */}
                {activeTab === 'build' && activeStrategy && !activeStrategyBuildLocked && (
                    <div style={{
                        position: 'fixed',
                        bottom: '2rem',
                        left: 'calc(var(--sidebar-width) + 2rem)',
                        right: '2rem',
                        zIndex: 200,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <AiAssistant
                            onSuccess={(node) => {
                                updateNodes([node]);
                            }}
                        />
                    </div>
                )}
            </div>
        </StrategyProvider>
    );
}
